import styled, { css, keyframes } from 'styled-components';

const PRIMARY = '#e4003a';
const TEXT_STRONG = '#1f2937';
const TEXT = '#4a4b4a';
const TEXT_MUTED = '#6b7280';
const TEXT_SOFT = '#c1c1c1';
const BORDER = '#eaecef';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 48px;
  font-family: 'Open Sans', sans-serif;
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;

  h1 {
    font-size: 22px;
    font-weight: 600;
    color: ${TEXT_STRONG};
    margin: 0;
  }

  p {
    font-size: 14px;
    color: ${TEXT};
    margin: 0;
  }
`;

export const Card = styled.section<{ padding?: number; muted?: boolean }>`
  background: ${({ muted }) => (muted ? '#f2f3f7' : '#fff')};
  border: 1px solid ${BORDER};
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
  padding: ${({ padding = 26 }) => padding}px;
  min-width: 0;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
`;

export const CardTitle = styled.div<{ nowrap?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  white-space: ${({ nowrap }) => (nowrap ? 'nowrap' : 'normal')};

  h2,
  h3 {
    margin: 0;
    font-weight: 600;
    color: ${TEXT_STRONG};
  }

  h2 {
    font-size: 16px;
  }

  h3 {
    font-size: 18px;
    line-height: 27px;
  }

  span {
    font-size: 12px;
    color: ${TEXT};
  }
`;

export const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  h2 {
    font-size: 22px;
    font-weight: 600;
    color: ${TEXT_STRONG};
    margin: 0;
  }
`;

export const PeriodChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 13px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  font-size: 12px;
  line-height: 18px;
  color: ${TEXT_MUTED};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 7px 15px;
  background: #f2f3f7;
  border: 1px solid rgba(231, 232, 236, 0.5);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  color: #191c1f;
  white-space: nowrap;
`;

// Controles / Alternador
export const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px;
  background: #f2f3f7;
  border: 1px solid rgba(231, 232, 236, 0.5);
  border-radius: 4px;
`;

export const SegmentedButton = styled.button<{ active: boolean }>`
  padding: 4px 12px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  color: #191c1f;
  white-space: nowrap;
  cursor: pointer;

  ${({ active }) =>
    active &&
    css`
      background: #fff;
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
    `}

  :disabled {
    color: #c1c1c1;
    cursor: not-allowed;
  }
`;

export const ChartMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 340px;
  padding: 0 24px;
  text-align: center;
  font-size: 14px;
  color: ${TEXT_MUTED};
`;

// Loading e erro dentro de cada card
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Spinner = styled.span<{ size?: number }>`
  display: inline-block;
  flex-shrink: 0;
  width: ${({ size = 28 }) => size}px;
  height: ${({ size = 28 }) => size}px;
  border: 3px solid ${BORDER};
  border-top-color: ${PRIMARY};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const CardStatus = styled.div<{ height: number; error?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: ${({ height }) => height}px;
  padding: 0 24px;
  text-align: center;
  font-size: 14px;
  color: ${({ error }) => (error ? PRIMARY : TEXT_MUTED)};
`;

// Filtros
export const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 36px;
`;

// Ajusta DatePicker e AutoCompleteSelectModal ao tamanho de texto do Figma.
export const FilterField = styled.div`
  && label {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  && input,
  && button[aria-haspopup] {
    font-size: 14px;
  }

  && button[aria-haspopup] {
    padding-bottom: 7px;
    border-bottom-color: ${TEXT_SOFT};

    svg {
      width: 22px;
      height: 22px;
      color: ${PRIMARY};
    }
  }

  && button[aria-haspopup][aria-expanded='true'] {
    border-bottom-color: #e71037;
  }
`;

export const FiltersActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  margin-top: 36px;

  button {
    width: 200px;
    height: 45px;
    border-radius: 4.5px;
    font-family: inherit;
    font-size: 15px;
    cursor: pointer;
  }
`;

export const OutlineButton = styled.button`
  background: #fff;
  border: 1px solid ${PRIMARY};
  color: ${PRIMARY};
`;

export const PrimaryButton = styled.button`
  background: ${PRIMARY};
  border: 1px solid ${PRIMARY};
  color: #fff;

  :disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// Indicadores
export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;
`;

export const KpiCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 165px;
  padding: 28px;
  background: #fff;
  border: 1.3px solid ${BORDER};
  border-radius: 10.5px;

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 18px;
    line-height: 28px;
    color: #4b5563;
  }

  img {
    width: 24px;
    height: 24px;
  }

  strong {
    display: block;
    font-size: 37px;
    font-weight: 700;
    line-height: 37px;
    color: ${TEXT_STRONG};
  }

  small {
    display: block;
    margin-top: 5px;
    font-size: 12px;
    line-height: 24px;
    color: #9ca3af;
  }
`;

export const KpiFooter = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 14px;
  padding-left: 5px;
  font-size: 10px;
  color: ${TEXT_MUTED};

  b {
    color: ${TEXT};
  }
`;

// Gráfico + telas
export const ChartRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 717fr) minmax(0, 440fr);
  gap: 18px;
  align-items: stretch;
`;

export const Controls = styled.div`
  display: flex;
  gap: 14px;
`;

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 35px;
  font-size: 11px;
  color: ${TEXT};

  ::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${PRIMARY};
  }
`;

export const ScrollList = styled.div<{ maxHeight: number }>`
  max-height: ${({ maxHeight }) => maxHeight}px;
  overflow: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${PRIMARY};
    border: 1px solid ${PRIMARY};
    border-radius: 9999px;
  }
`;

export const Progress = styled.div<{ value: number }>`
  height: 8px;
  border-radius: 9999px;
  background: #e5e7eb;
  overflow: hidden;

  ::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ value }) => Math.min(Math.max(value, 0), 100)}%;
    border-radius: 9999px;
    background: rgba(228, 0, 58, 0.7);
  }
`;

export const ScreenItem = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin-bottom: 6px;
  padding: 8px 10px;
  border: 1px solid ${({ selected }) => (selected ? PRIMARY : 'transparent')};
  border-radius: 8px;
  background: ${({ selected }) =>
    selected ? 'rgba(228, 0, 58, 0.05)' : 'transparent'};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  :hover {
    background: ${({ selected }) =>
      selected ? 'rgba(228, 0, 58, 0.08)' : '#f9fafb'};
  }

  :focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
  }

  header {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    line-height: 16px;
    color: ${TEXT};

    span {
      font-weight: 600;
    }

    b {
      font-weight: 700;
    }
  }

  small {
    font-size: 10px;
    line-height: 12px;
    color: ${TEXT_MUTED};
  }
`;

// Serviços de API
export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 30px;
`;

export const ServiceItem = styled.div`
  display: grid;
  grid-template-columns: 21px 1fr auto auto;
  column-gap: 0;
  row-gap: 10px;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  .rank {
    color: ${TEXT_SOFT};
  }

  .name {
    color: ${TEXT};
  }

  .total {
    color: ${TEXT};
    margin-right: 14px;
  }

  .percent {
    color: ${TEXT_MUTED};
    min-width: 38px;
    text-align: right;
  }

  ${Progress} {
    grid-column: 2 / -1;
  }
`;

// Marcadores
export const MarkersBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

export const MarkersList = styled.div`
  display: flex;
  align-items: center;
  gap: 45px;
`;

export const Marker = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${TEXT};

  ::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ color }) => color};
  }

  b {
    font-weight: 600;
    color: #000;
  }

  small {
    font-size: 10px;
  }
`;

export const ErrorBox = styled.div`
  padding: 16px 20px;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  background: #fdf0f2;
  font-size: 14px;
  color: ${PRIMARY};
`;

// Tabelas
export const TablesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;
`;

export const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;

  ${CardHeader} {
    padding: 31px 18px 0;
    margin-bottom: 20px;
  }
`;

export const Table = styled.table<{ minWidth?: number }>`
  width: 100%;
  min-width: ${({ minWidth = 0 }) => minWidth}px;
  border-collapse: collapse;
  font-size: 12px;

  th {
    height: 40px;
    padding: 0 18px;
    background: #f9f9f9;
    border-bottom: 0.8px solid #f1f1f1;
    font-weight: 600;
    text-align: left;
    color: ${TEXT_MUTED};
    white-space: nowrap;
    position: sticky;
    top: 0;
  }

  td {
    padding: 12px 18px;
    color: ${TEXT};
    vertical-align: top;
    white-space: nowrap;
  }

  td small {
    display: block;
    margin-top: 4px;
    font-size: 10px;
    color: ${TEXT_SOFT};
  }

  .center {
    text-align: center;
  }

  .url {
    width: 270px;
    font-size: 10px;
    color: #757575;
    white-space: normal;
    word-break: break-all;
  }
`;

// Modal de chamadas da visita
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.4);
  animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalSheet = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1080px;
  max-height: 85vh;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.15);
  font-family: 'Open Sans', sans-serif;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  h3 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    color: ${TEXT_STRONG};
  }

  span {
    display: block;
    font-size: 12px;
    color: ${TEXT};
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    color: ${TEXT_SOFT};
  }

  button {
    display: flex;
    padding: 4px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${TEXT};
    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;
    }

    :focus-visible {
      outline: 2px solid ${PRIMARY};
    }
  }
`;

export const ModalSummary = styled.p`
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${TEXT_MUTED};
`;

export const ClickableRow = styled.tr<{ selected?: boolean }>`
  cursor: pointer;
  background: ${({ selected }) =>
    selected ? 'rgba(228, 0, 58, 0.05)' : 'transparent'};

  :hover {
    background: ${({ selected }) =>
      selected ? 'rgba(228, 0, 58, 0.08)' : '#f9fafb'};
  }

  :focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: -2px;
  }
`;
